// THE FULL CONTACT PAGE, /contacts/<id> (Carla, 2026-10-21).
//
// The pop-up became a quick summary; everything it used to carry, and all of the history, is here,
// on the v2 kit: the header (← Contacts, the name, "Edit contact" as a secondary header button, decision 88 rewritten);
// the same actions, key details and tiles as the pop-up (one set of components, ContactDrawer.jsx);
// then TABS, so nothing is one long scroll: Activity, Notes, Meetings, Invoices, Proposals and
// contracts, Tasks, Portal, Intake answers. A tab row on desktop; on a phone the kit's TabGroup is a
// tab bar that scrolls sideways with snap (addendum 4). Each tab is a DashboardCard (decision 89) with
// its header action as a text link (decision 88), and lists with "View" into the page that owns it.
//
// EVERY TAB SAYS WHAT HAPPENED: loading, a failure with Try again (never an empty list), empty in
// words, and the list. The lists are paged on the server (GET /api/contacts/:id/activity and
// /documents/:kind), read through the same scopes as the pop-up's record (decision 30: an entry
// about a document this person may not open says only that it happened).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import { messageFromError } from '../../utils/apiError';
import { formatDateTime } from '../../utils/formatting';
import { invoiceStatusWord } from '../../utils/invoiceOverdue';
import {
  PageLayout, PageHeader, HeaderAction, DashboardCard, TabGroup, TabPanel, Button, ChipGroup, SearchInput,
  DateInput, Field, EmptyState, ErrorState, Skeleton, StatusPill, Avatar,
} from '../../ui';
import ContactRecord, { Row, ContractRow, fmtDate, eventLabel, ActivityDot, CATEGORY_TINT } from './ContactRecord';
import ContactComposer from './ContactComposer';
import LinkedNotes from '../Notes/LinkedNotes';
import {
  ContactActions, ContactFacts, ContactTiles, portalLabelOf, bookCallSeed, usePortalEnable, fmtLong,
} from './ContactDrawer';
import { tagColor, primaryTag, orderedTags } from './contactTags';
import { navigateTo, newInvoiceFor } from './contactActions';
import './Contacts.css';

// The Activity filters: the server's categories (Aidey Backend/lib/contactActivity.js), in its order.
export const ACTIVITY_CATEGORIES = [
  { value: 'all', label: 'All' },
  { value: 'emails', label: 'Emails' },
  { value: 'bookings', label: 'Bookings' },
  { value: 'invoices', label: 'Invoices' },
  { value: 'contracts', label: 'Contracts' },
  { value: 'proposals', label: 'Proposals' },
  { value: 'portal', label: 'Portal' },
  { value: 'notes', label: 'Notes' },
  { value: 'logged', label: 'Logged contacts' },
];
const PAGE_SIZE = 20;

// The start or end of the person's own day, as an instant: the server filters on instants, so a
// date-only range never shifts a day for someone outside UTC.
export function dayEdge(ymd, edge) {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  const at = edge === 'to' ? new Date(y, m - 1, d, 23, 59, 59, 999) : new Date(y, m - 1, d, 0, 0, 0, 0);
  return Number.isNaN(at.getTime()) ? '' : at.toISOString();
}

// The server's words end a sentence before ours begins ("Not found" + "This is not an empty list.").
const asSentence = (s) => { const t = String(s || '').trim(); return /[.!?]$/.test(t) ? t : `${t}.`; };

// One paged read: { status: 'loading' | 'ready' | 'failed', data, error, reload }.
function usePaged(url) {
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const load = useCallback(() => {
    if (!url) return undefined;
    let alive = true;
    setState((s) => ({ ...s, status: 'loading', error: null }));
    api(url)
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        if (!d || !Array.isArray(d.items)) throw new Error('Aidey sent back something it could not read.');
        setState({ status: 'ready', data: d, error: null });
      })
      .catch((e) => { if (alive) setState({ status: 'failed', data: null, error: asSentence(messageFromError(e, 'This list did not load.')) }); });
    return () => { alive = false; };
  }, [url]);
  useEffect(() => load(), [load]);
  return { ...state, reload: load };
}

function Pager({ data, page, onPage, noun = 'entries' }) {
  if (!data || data.total <= data.pageSize) return null;
  const first = (page - 1) * data.pageSize + 1;
  const last = first + data.items.length - 1;
  return (
    <div className="contacts-pager" role="navigation" aria-label={`More ${noun}`}>
      <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>← Newer</Button>
      <span className="contacts-pager__count" aria-live="polite">{first}–{last} of {data.total}</span>
      <Button variant="secondary" size="sm" disabled={!data.hasMore} onClick={() => onPage(page + 1)}>Older →</Button>
    </div>
  );
}

function ViewLink({ onClick, label }) {
  return <button type="button" className="contacts-section__link contacts-viewlink" onClick={onClick} aria-label={label}>View</button>;
}

// ── Activity ──────────────────────────────────────────────────────────────────────────────────
export function ActivityTab({ contact, onLog, version }) {
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const debounce = useRef(null);
  const onSearch = (value) => {
    setQ(value);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => { setQuery(value.trim()); setPage(1); }, 300);
  };
  useEffect(() => () => clearTimeout(debounce.current), []);
  const rangeBad = from && to && from > to;
  const url = useMemo(() => {
    if (rangeBad) return null;
    const p = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
    if (category !== 'all') p.set('category', category);
    if (query) p.set('q', query);
    if (from) p.set('from', dayEdge(from, 'from'));
    if (to) p.set('to', dayEdge(to, 'to'));
    return `/api/contacts/${contact.id}/activity?${p.toString()}`;
  }, [contact.id, page, category, query, from, to, rangeBad]);
  const list = usePaged(url);
  // Something logged or sent from this page changes the record; the history reads again.
  const seen = useRef(version);
  useEffect(() => {
    if (seen.current === version) return;
    const first = !seen.current;   // the record's first load is not a change
    seen.current = version;
    if (!first && version) list.reload();
  }, [version]); // eslint-disable-line react-hooks/exhaustive-deps
  const filtered = category !== 'all' || !!query || !!from || !!to;
  const clear = () => { setQ(''); setQuery(''); setCategory('all'); setFrom(''); setTo(''); setPage(1); };
  const data = list.data;

  return (
    <DashboardCard title="Activity" tint="pastel-blue" data-testid="contact-tab-activity"
      meta={<>{data && <span className="contacts-meta">{data.total} {data.total === 1 ? 'entry' : 'entries'}</span>}{onLog && <HeaderAction onClick={onLog}>Log contact</HeaderAction>}</>}>
      <div className="contacts-activity__filters">
        <SearchInput value={q} onChange={(e) => onSearch(e.target.value)} placeholder="Search the history…" label="Search the history" />
        <ChipGroup label="Show" value={category} onChange={(c) => { setCategory(c); setPage(1); }}
          options={ACTIVITY_CATEGORIES.map((c) => ({ ...c, color: c.value === 'all' ? undefined : CATEGORY_TINT[c.value] }))} />
        <div className="contacts-activity__range">
          <Field label="From"><DateInput value={from} max={to || undefined} onChange={(e) => { setFrom(e.target.value); setPage(1); }} /></Field>
          <Field label="To"><DateInput value={to} min={from || undefined} onChange={(e) => { setTo(e.target.value); setPage(1); }} /></Field>
          {filtered && <button type="button" className="contacts-section__link" onClick={clear}>Clear filters</button>}
        </div>
      </div>
      {rangeBad ? (
        <p className="contacts-error" role="alert">The From date is after the To date.</p>
      ) : list.status === 'failed' ? (
        <ErrorState title="The history didn't load" message={`${list.error} This is not an empty history.`} onRetry={list.reload} />
      ) : list.status === 'loading' && !data ? (
        <Skeleton lines={4} label="Loading the history" />
      ) : data.items.length === 0 ? (
        filtered
          ? <EmptyState title="Nothing matches">No entries match these filters. <button type="button" className="contacts-section__link" onClick={clear}>Clear filters</button></EmptyState>
          : <EmptyState title="Nothing recorded yet">Log a call, send an email, or open a portal and it will appear here.</EmptyState>
      ) : (
        <ul className="contacts-activity" data-testid="contact-activity-list" aria-busy={list.status === 'loading'}>
          {data.items.map((i) => (
            <li key={i.id} className="contacts-activity__row" data-category={i.category}>
              <ActivityDot type={i.type} />
              <span className="contacts-activity__text">
                {i.redacted ? eventLabel(i.type) : (i.summary || eventLabel(i.type))}
                {!i.redacted && <span className="contacts-muted"> · {String(i.type).replace(/_/g, ' ')}</span>}
              </span>
              <span className="contacts-activity__when">{fmtDate(i.occurredAt)}</span>
            </li>
          ))}
        </ul>
      )}
      {data?.truncated && <p className="contacts-help">Showing the first 1,000 matches. Narrow the search to see the rest.</p>}
      <Pager data={data} page={page} onPage={setPage} />
    </DashboardCard>
  );
}

// ── A paged list of one kind of document ──────────────────────────────────────────────────────
function DocumentsCard({ contact, kind, title, tint, action, empty, renderRow, testid }) {
  const [page, setPage] = useState(1);
  const list = usePaged(`/api/contacts/${contact.id}/documents/${kind}?page=${page}&pageSize=${PAGE_SIZE}`);
  const data = list.data;
  return (
    <DashboardCard title={title} tint={tint} data-testid={testid || `contact-tab-${kind}`}
      meta={<>
        {/* A questionnaire's count is ANSWERS: "4" beside one answer and three waiting over-claims. */}
        {data && <span className="contacts-meta" data-testid={`${kind}-count`}>{kind === 'forms' ? `${data.answeredCount ?? 0} answered` : data.total}</span>}
        {action}
      </>}>
      {list.status === 'failed' ? (
        <ErrorState title={`${title} didn't load`} message={`${list.error} This is not an empty list.`} onRetry={list.reload} />
      ) : list.status === 'loading' && !data ? (
        <Skeleton lines={3} label={`Loading ${title.toLowerCase()}`} />
      ) : data.items.length === 0 ? (
        <EmptyState title={empty.title}>{empty.text}</EmptyState>
      ) : (
        <div className="contacts-doclist">{data.items.map(renderRow)}</div>
      )}
      <Pager data={data} page={page} onPage={setPage} noun={title.toLowerCase()} />
      {kind === 'forms' && data?.waitingCount > 0 && (
        <p className="contacts-help">{data.waitingCount === 1 ? '1 still waiting on them' : `${data.waitingCount} still waiting on them`}</p>
      )}
    </DashboardCard>
  );
}

const open = (path) => () => navigateTo(path);

export function MeetingsTab({ contact }) {
  return (
    <DocumentsCard contact={contact} kind="meetings" title="Meetings" tint="pastel-green"
      action={<HeaderAction onClick={open(`/scheduler?bookings_contact=${encodeURIComponent(contact.id)}`)}>Open in Scheduler</HeaderAction>}
      empty={{ title: 'No meetings yet', text: `Nothing booked with ${contact.name} yet.` }}
      renderRow={(m) => (
        <Row key={m.id} left={<>{m.meetingType?.name || 'Meeting'} <span className="contacts-muted">· {formatDateTime(m.startTime)}</span></>}
          right={m.status === 'cancelled' ? 'Cancelled' : new Date(m.startTime) > new Date() ? 'Upcoming' : 'Past'}
          action={<ViewLink label={`View the meeting on ${fmtDate(m.startTime)}`} onClick={open(`/scheduler?booking=${encodeURIComponent(m.id)}`)} />} />
      )} />
  );
}

export function InvoicesTab({ contact, isPersonal }) {
  return (
    <DocumentsCard contact={contact} kind="invoices" title="Invoices" tint="pastel-pink"
      action={isPersonal ? null : <HeaderAction onClick={() => newInvoiceFor(contact.id)}>+ New invoice</HeaderAction>}
      empty={{ title: 'No invoices yet', text: `Nothing invoiced to ${contact.name} that you can see.` }}
      renderRow={(i) => (
        <Row key={i.id} left={<>{i.number || 'Draft'}{i.dueDate ? <span className="contacts-muted"> · due {fmtDate(i.dueDate)}</span> : null}</>}
          right={invoiceStatusWord(i)}
          action={<ViewLink label={`View invoice ${i.number || ''}`} onClick={open(`/invoices?invoice=${encodeURIComponent(i.id)}`)} />} />
      )} />
  );
}

// Email Phase 1 (Carla, 2026-10-21): the emails with this contact, from the viewer's own mailboxes
// (stored emails linked by lib/emailLinks; a colleague's mailbox is never shown). No bodies.
export function emailLine(e) {
  return e.direction === 'outbound' ? `To ${e.toEmails || 'them'}` : `From ${e.fromName || e.fromEmail || 'them'}`;
}
export function EmailsTab({ contact }) {
  return (
    <DocumentsCard contact={contact} kind="emails" title="Emails" tint="pastel-blue"
      action={<HeaderAction onClick={open('/emails')}>Open Emails</HeaderAction>}
      empty={{ title: 'No emails yet', text: `No emails with ${contact.name} in your mailboxes yet.` }}
      renderRow={(e) => (
        <Row key={e.id} left={<><span>{e.subject || '(no subject)'}</span> <span className="contacts-muted">· {emailLine(e)}{e.preview ? ` · ${e.preview}` : ''}</span></>}
          right={fmtDate(e.sentAt)} />
      )} />
  );
}

export function DocumentsTab({ contact }) {
  return (
    <div className="contacts-page__stack">
      <DocumentsCard contact={contact} kind="proposals" title="Proposals" tint="pastel-yellow"
        action={<HeaderAction onClick={open(`/proposals?proposals_contact=${encodeURIComponent(contact.id)}`)}>Open in Proposals</HeaderAction>}
        empty={{ title: 'No proposals yet', text: `No proposals to ${contact.name} that you can see.` }}
        renderRow={(p) => (
          <Row key={p.id} left={p.title} right={String(p.status || '').replace(/^./, (c) => c.toUpperCase())}
            action={<ViewLink label={`View proposal ${p.title}`} onClick={open(`/proposals?proposal=${encodeURIComponent(p.id)}`)} />} />
        )} />
      {/* TIER 2 (Carla, 2026-09-23): a signed contract is findable from the client without knowing it
          exists, downloadable, and says at a glance whether it is COMPLETE. */}
      <DocumentsCard contact={contact} kind="contracts" title="Contracts" tint="pastel-lilac"
        action={<HeaderAction onClick={open('/contracts')}>Open in Contracts</HeaderAction>}
        empty={{ title: 'No contracts yet', text: `No contracts with ${contact.name} that you can see.` }}
        renderRow={(c) => (
          <div key={c.id} className="contacts-docrow">
            <ContractRow c={c} />
            <ViewLink label={`View contract ${c.title}`} onClick={open(`/contracts?contract=${encodeURIComponent(c.id)}`)} />
          </div>
        )} />
    </div>
  );
}

export function TasksTab({ contact }) {
  return (
    <DocumentsCard contact={contact} kind="tasks" title="Tasks" tint="pastel-peach"
      empty={{ title: 'No tasks yet', text: `None of your tasks are for ${contact.name}.` }}
      renderRow={(t) => (
        <Row key={t.id} left={t.title} right={t.completed ? 'Done' : (t.deadline || t.date ? `Due ${fmtDate(t.deadline || t.date)}` : 'No date')}
          action={<ViewLink label={`View task ${t.title}`} onClick={open(`/tasks?task=${encodeURIComponent(t.id)}`)} />} />
      )} />
  );
}

export function PortalTab({ contact, recordState, portal }) {
  const r = recordState.record;
  let body;
  if (recordState.error) {
    body = <ErrorState title="The portal didn't load" message={`${recordState.error} This does not mean they have no portal.`} onRetry={recordState.reload} />;
  } else if (!r) {
    body = <Skeleton lines={2} label="Loading the portal" />;
  } else if (!r.portal) {
    body = (
      <EmptyState title="No portal yet" action={
        <Button variant="confirm" size="sm" disabled={portal.portalStatus === 'loading'} onClick={async () => { await portal.enable(); recordState.reload?.(); }}>
          {portal.portalStatus === 'loading' ? 'Enabling…' : 'Enable portal'}
        </Button>
      }>
        {contact.email ? 'Enabling one sends them an invite.' : 'This contact has no email address, so no invite will be sent.'}
      </EmptyState>
    );
  } else {
    body = (
      <dl className="contacts-facts">
        <div className="contacts-facts__row"><dt>Status</dt><dd>{r.portal.isActive ? 'Active' : 'Inactive'}</dd></div>
        <div className="contacts-facts__row"><dt>Last opened</dt><dd>{r.portal.lastAccessedAt ? fmtDate(r.portal.lastAccessedAt) : 'Never'}</dd></div>
        <div className="contacts-facts__row"><dt>Messages</dt><dd>{r.portal.messageCount}</dd></div>
        <div className="contacts-facts__row"><dt>Files</dt><dd>{r.portal.fileCount}</dd></div>
        <div className="contacts-facts__row"><dt>Opened for them</dt><dd>{fmtDate(r.portal.createdAt)}</dd></div>
      </dl>
    );
  }
  return (
    <DashboardCard title="Portal" tint="pastel-orange" data-testid="contact-tab-portal"
      meta={r?.portal ? <>
        <HeaderAction onClick={open(`/portal?portal_messages=${encodeURIComponent(r.portal.id)}`)}>Messages</HeaderAction>
        <HeaderAction onClick={open(`/portal?portal=${encodeURIComponent(r.portal.id)}`)}>Open portal</HeaderAction>
      </> : null}>
      {portal.portalStatus === 'invited' && <p className="contacts-ok" role="status">{contact.email ? `Portal activated and invite sent to ${contact.email}` : 'Portal activated'}</p>}
      {portal.portalStatus === 'error' && <p className="contacts-error" role="alert">{portal.portalError}</p>}
      {body}
    </DashboardCard>
  );
}

export function IntakeTab({ contact }) {
  const snaps = Array.isArray(contact.intakeAnswers) ? [...contact.intakeAnswers].reverse() : [];
  return (
    <div className="contacts-page__stack">
      {/* Intake answers (Block 151 / D27 #4): snapshots from booking forms, newest first. */}
      <DashboardCard title="Intake answers" tint="pastel-mint" data-testid="contact-tab-intake">
        {snaps.length === 0 ? (
          <EmptyState title="No intake answers">Answers to your booking forms appear here.</EmptyState>
        ) : snaps.map((snap, i) => (
          <div key={i} className="contacts-note">
            <span className="contacts-muted">{snap.submittedAt ? fmtLong(snap.submittedAt) : ''}</span>
            {Object.entries(snap.answers || {}).map(([label, value]) => (
              <div key={label}><strong>{label}:</strong> {String(value)}</div>
            ))}
          </div>
        ))}
      </DashboardCard>
      {/* 3.1: QUESTIONNAIRES. SENT AND ANSWERED ARE NOT THE SAME ROW. */}
      <DocumentsCard contact={contact} kind="forms" title="Questionnaires" tint="pastel-periwinkle" testid="contact-tab-forms"
        action={<HeaderAction onClick={open('/forms')}>Open in Forms</HeaderAction>}
        empty={{ title: 'No questionnaires yet', text: `Nothing sent to ${contact.name} yet.` }}
        renderRow={(f) => (
          <Row key={f.id} left={f.title}
            right={f.answered ? `answered ${fmtDate(f.completedAt)}` : `sent ${fmtDate(f.sentAt)}, not answered yet`}
            action={f.formId ? <ViewLink label={`View ${f.title}`} onClick={open(`/forms?form=${encodeURIComponent(f.formId)}`)} /> : null} />
        )} />
    </div>
  );
}

export function NotesTab({ contact }) {
  return (
    <div className="contacts-page__stack">
      <DashboardCard title="Notes" tint="pastel-periwinkle" data-testid="contact-tab-notes">
        <LinkedNotes kind="contact" id={contact.id} title="Linked notes"
          emptyText={`No notes linked to ${contact.name || 'this contact'} yet.`} />
      </DashboardCard>
      {contact.notes && (
        <DashboardCard title="About this contact" tint="pastel-neutral">
          <p className="contacts-note">{contact.notes}</p>
        </DashboardCard>
      )}
    </div>
  );
}

// ── The page ──────────────────────────────────────────────────────────────────────────────────
export default function ContactPage({ contact, isPersonal, tagColors, crm, tab, onTab, onBack, onEdit }) {
  const [recordState, setRecordState] = useState({ record: null, error: null, loading: true });
  const [panel, setPanel] = useState(null);
  const [compose, setCompose] = useState(null);
  const [booking, setBooking] = useState(false);
  const portal = usePortalEnable(contact);
  const primary = primaryTag(contact, isPersonal);
  const tags = orderedTags(contact.tags);
  const r = recordState.record;
  useEffect(() => { setCompose(null); setPanel(null); }, [contact.id]);
  useEffect(() => { try { window.scrollTo(0, 0); } catch { /* jsdom */ } }, [contact.id]);

  const tabs = [
    { id: 'activity', label: 'Activity', color: 'pastel-blue' },
    { id: 'notes', label: 'Notes', color: 'pastel-periwinkle' },
    ...(isPersonal ? [] : [
      { id: 'meetings', label: 'Meetings', color: 'pastel-green', count: r?.meetings?.count },
      { id: 'invoices', label: 'Invoices', color: 'pastel-pink', count: r?.invoices?.count },
      { id: 'documents', label: 'Proposals and contracts', color: 'pastel-yellow', count: r ? (r.proposals?.count || 0) + (r.contracts?.count || 0) : undefined },
    ]),
    { id: 'emails', label: 'Emails', color: 'pastel-blue' },
    { id: 'tasks', label: 'Tasks', color: 'pastel-peach', count: r?.tasks?.count },
    ...(isPersonal ? [] : [
      { id: 'portal', label: 'Portal', color: 'pastel-orange' },
      { id: 'intake', label: 'Intake answers', color: 'pastel-mint', count: r?.forms?.count || undefined },
    ]),
  ];
  const current = tabs.some((t) => t.id === tab) ? tab : 'activity';

  async function bookCall() {
    setBooking(true);
    const seed = await bookCallSeed(contact);
    setBooking(false);
    if (seed) setCompose({ seed, title: 'Book a call' });
  }

  return (
    <PageLayout data-page="contact" data-testid="contact-page">
      <button type="button" className="contacts-back" onClick={onBack}><span aria-hidden="true">← </span>Contacts</button>
      <PageHeader
        title={contact.name}
        subline={[contact.company, ...tags].filter(Boolean).join(' · ') || (isPersonal ? null : 'No company')}
        secondary={onEdit ? [{ label: 'Edit contact', onClick: onEdit }] : []}
      />

      {compose ? (
        <DashboardCard title={compose.title} tint="pastel-blue">
          <ContactComposer contact={contact} seed={compose.seed} title={compose.title}
            onBack={() => setCompose(null)} onSent={() => setCompose(null)} />
        </DashboardCard>
      ) : (
        <div className="contacts-page__top">
          <DashboardCard title="Details" tint={tagColor(primary, tagColors)} data-testid="contact-page-details">
            <div className="contacts-page__who">
              <Avatar name={contact.name} size={44} color={tagColor(primary, tagColors)} />
              <span className="contacts-pills">
                {contact.clientPortal && <span className="contacts-portal-pill">Portal</span>}
                {contact.isNew && <StatusPill tone="pending">New</StatusPill>}
              </span>
            </div>
            <ContactActions contact={contact} isPersonal={isPersonal} booking={booking}
              onEmail={() => setCompose({ seed: null, title: 'New email' })} onBookCall={bookCall}
              onInvoice={() => newInvoiceFor(contact.id)} />
            <ContactFacts contact={contact} isPersonal={isPersonal} portalLabel={portalLabelOf(recordState)} crm={crm}
              onEmail={() => setCompose({ seed: null, title: 'New email' })} />
          </DashboardCard>
          <DashboardCard title="At a glance" tint="pastel-neutral">
            {!isPersonal && <ContactTiles recordState={recordState} />}
            <ContactRecord contact={contact} onEnablePortal={portal.enable} portalStatus={portal.portalStatus}
              panel={panel} onPanelChange={setPanel} onState={setRecordState} showHistory={false} />
          </DashboardCard>
        </div>
      )}

      <TabGroup idPrefix="contact-page" label={`${contact.name}: sections`} tabs={tabs} value={current} onChange={onTab} />
      <TabPanel idPrefix="contact-page" tabId={current}>
        {current === 'activity' && <ActivityTab contact={contact} onLog={() => setPanel('log')} version={r} />}
        {current === 'notes' && <NotesTab contact={contact} />}
        {current === 'meetings' && <MeetingsTab contact={contact} />}
        {current === 'invoices' && <InvoicesTab contact={contact} isPersonal={isPersonal} />}
        {current === 'documents' && <DocumentsTab contact={contact} />}
        {current === 'emails' && <EmailsTab contact={contact} />}
        {current === 'tasks' && <TasksTab contact={contact} />}
        {current === 'portal' && <PortalTab contact={contact} recordState={recordState} portal={portal} />}
        {current === 'intake' && <IntakeTab contact={contact} />}
      </TabPanel>
    </PageLayout>
  );
}

